import {
  Button,
  Col,
  Dropdown,
  Input,
  ModalV2,
  Row,
  Switch,
  Text,
  TextArea,
  Upload,
  UploadButton
} from 'components';
import ObjectId from 'bson-objectid';
import { Api, theme, uploadWithSignedUrl } from 'lib';
import React, { useRef, useState } from 'react';
import styled from 'styled-components';

const SectionTitle = ({ children }) => (
  <Text h4 bold marg="0 0 16px" col={theme.GREY_60}>
    {children}
  </Text>
);

const CreateRewardGiftModal = ({ team, onClose, onSuccess, giftToEdit }) => {
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const textAreaRef = useRef(null);
  const fileRef = useRef(null);

  const [form, setForm] = useState({
    name: giftToEdit?.name || '',
    description: giftToEdit?.description || '',
    image: giftToEdit?.image || '',
    pointCost: giftToEdit?.pointCost || '',
    limitTotal: giftToEdit?.limitTotal || '',
    limitPerUser: giftToEdit?.limitPerUser || '',
    tier: giftToEdit?.tier || 'basic',
    isActive: giftToEdit ? giftToEdit.isActive : true,
    isUnlimitedTotal: giftToEdit ? !giftToEdit.limitTotal : true,
    isUnlimitedPerUser: giftToEdit ? !giftToEdit.limitPerUser : true
  });

  const tiers = [
    { name: 'Basic', value: 'basic' },
    { name: 'Active', value: 'active' },
    { name: 'Elite', value: 'elite' }
  ];

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSwitchChange = (field, value) => {
    setForm((prev) => ({
      ...prev,
      [field]: value,
      // Clear input if setting to unlimited
      ...(value === true && field === 'isUnlimitedTotal' ? { limitTotal: '' } : {}),
      ...(value === true && field === 'isUnlimitedPerUser' ? { limitPerUser: '' } : {})
    }));
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate type if needed
    if (!file.type.includes('image')) {
      alert('Please upload an image file');
      return;
    }

    setUploading(true);
    try {
      const extension = file.type?.split('/')[1] || 'jpeg';
      /*
        Keys must stay flat: the server persists media as a bare filename
        (see lib/files/mediaUrl.ts) and strips any prefix, so a nested key
        would be saved pointing at a path the object does not live at.
      */
      const key = `${ObjectId().toString()}.${extension}`;
      const url = await uploadWithSignedUrl(key, file.type, file);

      if (url) {
        handleChange('image', url);
      } else {
        alert('Upload failed');
      }
    } catch (err) {
      console.error(err);
      alert('Upload error');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!form.name || !form.pointCost || !form.tier) {
      alert('Please fill in required fields (Name, Point Cost, Tier)');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: form.name,
        description: form.description,
        image: form.image,
        tier: form.tier,
        isActive: form.isActive,
        pointCost: Number(form.pointCost),
        limitTotal: !form.isUnlimitedTotal && form.limitTotal ? Number(form.limitTotal) : 0,
        limitPerUser: !form.isUnlimitedPerUser && form.limitPerUser ? Number(form.limitPerUser) : 0
      };

      let res;
      if (giftToEdit) {
        payload.id = giftToEdit._id;
        res = await Api.put('/admin/rewardGifts/update', payload);
      } else {
        payload.teamId = team._id;
        res = await Api.post('/admin/rewardGifts/create', payload);
      }

      if (res?.data || res?.msg === 'Reward gift updated successfully') {
        onSuccess(res.data);
        onClose();
      }
    } catch (e) {
      console.error(e);
      alert(giftToEdit ? 'Failed to update reward gift' : 'Failed to create reward gift');
    } finally {
      setLoading(false);
    }
  };

  const isMobile = global.isMobile;

  return (
    <ModalV2
      visible={true}
      closeModal={onClose}
      width="600px"
      header={
        <Text h2 marg="0 24px 0 0">
          {giftToEdit ? 'Edit Reward Gift' : 'Create Reward Gift'}
        </Text>
      }
      footer={
        <Row center justifyContent="flex-end">
          <Button
            text="Cancel"
            onClick={onClose}
            bordered
            marg="0 16px 0 0"
            style={{ width: 'fit-content', opacity: 0.8 }}
          />
          <Button
            text={
              loading
                ? giftToEdit
                  ? 'Updating...'
                  : 'Creating...'
                : giftToEdit
                  ? 'Update Gift'
                  : 'Create Gift'
            }
            onClick={handleSubmit}
            disabled={loading || uploading}
            col={theme.PRIMARY}
            style={{ width: 'fit-content' }}
          />
        </Row>
      }
    >
      <Col>
        <Col wid="100%">
          {/* General Info Section */}
          <SectionTitle>General Information</SectionTitle>

          <Col wid="100%" marg="0 0 16px">
            <Input
              title="Name *"
              placeholder="Enter gift name"
              value={form.name}
              onChange={(e) => handleChange('name', e.target.value)}
              wid="100%"
            />
          </Col>

          <Col wid="100%" marg="0 0 16px">
            <Text size={isMobile ? '18px' : '20px'} col={theme.SECONDARY} med marg="0 0 10px">
              Image
            </Text>
            <Upload elemRef={fileRef} handleFile={handleFile} />
            {form.image ? (
              <Col>
                <img
                  src={form.image}
                  alt="Reward Gift"
                  style={{
                    width: 100,
                    height: 100,
                    objectFit: 'cover',
                    borderRadius: 8,
                    marginBottom: 8,
                    backgroundColor: theme.GREY_10,
                    display: 'block'
                  }}
                />
                <Button
                  text={uploading ? 'Uploading...' : 'Change Image'}
                  onClick={() => !uploading && fileRef.current.click()}
                  style={{ width: 'fit-content', padding: '0 16px', height: 40 }}
                  bordered
                  disabled={uploading}
                />
              </Col>
            ) : (
              <Col>
                <UploadButton onClick={() => !uploading && fileRef.current.click()} />
                {uploading && (
                  <Text size="14px" col={theme.GREY_60} marg="8px 0 0">
                    Uploading...
                  </Text>
                )}
              </Col>
            )}
          </Col>

          <Col wid="100%" marg="0 0 32px">
            <Text marg="0 0 10px" size={isMobile ? '18px' : '20px'} col={theme.SECONDARY} med>
              Description
            </Text>
            <TextArea
              ref={textAreaRef}
              placeholder="Enter description..."
              value={form.description}
              onChange={(e) => handleChange('description', e.target.value)}
              ht="100px"
              hasRadius="15px"
              border="none"
              bg={theme.GREY_10} // Using GREY_10 for simpler background within Modal content
              style={{
                fontSize: 18,
                fontFamily: 'CeraPro',
                padding: 19,
                width: '100%',
                minWidth: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box'
              }}
            />
          </Col>

          {/* Configuration Section */}
          <SectionTitle>Configuration</SectionTitle>

          <Col wid="100%" marg="0 0 16px">
            <Row between center marg="0 0 8px">
              <Text size={isMobile ? '18px' : '20px'} col={theme.SECONDARY} med>
                Is Active
              </Text>
              <Switch
                activated={form.isActive}
                onClick={() => handleSwitchChange('isActive', !form.isActive)}
              />
            </Row>
          </Col>

          <Col wid="100%" marg="0 0 16px">
            <Input
              title="Point Cost *"
              type="number"
              placeholder="0"
              value={form.pointCost}
              onChange={(e) => handleChange('pointCost', e.target.value)}
              wid="100%"
            />
          </Col>

          <Col wid="100%" marg="0 0 32px">
            <Dropdown
              rounded
              title="Tier *"
              placeholder="Select Tier"
              options={tiers}
              selected={tiers.find((t) => t.value === form.tier)}
              handleSelect={(item) => handleChange('tier', item.value)}
              wid="100%"
            />
          </Col>

          {/* Restrictions Section */}
          <SectionTitle>Restrictions</SectionTitle>

          <Col wid="100%" marg="0 0 16px">
            <Row between center marg="0 0 8px">
              <Text size={isMobile ? '18px' : '20px'} col={theme.SECONDARY} med>
                Total Limit
              </Text>
              <Row center noFlex>
                <Text b2 med marg="0 8px 0 0" col={theme.GREY_60}>
                  Unlimited
                </Text>
                <Switch
                  activated={form.isUnlimitedTotal}
                  onClick={() => handleSwitchChange('isUnlimitedTotal', !form.isUnlimitedTotal)}
                />
              </Row>
            </Row>
            {!form.isUnlimitedTotal && (
              <Input
                type="number"
                placeholder="Enter total limit"
                value={form.limitTotal}
                onChange={(e) => handleChange('limitTotal', e.target.value)}
                wid="100%"
              />
            )}
          </Col>

          <Col wid="100%">
            <Row between center marg="0 0 8px">
              <Text size={isMobile ? '18px' : '20px'} col={theme.SECONDARY} med>
                Limit Per User
              </Text>
              <Row center noFlex>
                <Text b2 med marg="0 8px 0 0" col={theme.GREY_60}>
                  Unlimited
                </Text>
                <Switch
                  activated={form.isUnlimitedPerUser}
                  onClick={() => handleSwitchChange('isUnlimitedPerUser', !form.isUnlimitedPerUser)}
                />
              </Row>
            </Row>
            {!form.isUnlimitedPerUser && (
              <Input
                type="number"
                placeholder="Enter limit per user"
                value={form.limitPerUser}
                onChange={(e) => handleChange('limitPerUser', e.target.value)}
                wid="100%"
              />
            )}
          </Col>
        </Col>
      </Col>
    </ModalV2>
  );
};

export default CreateRewardGiftModal;
